import {
  instance as axios,
  setJwtToken,
} from '../../config/axiosInstance'

// ==================== ENHANCED NOTIFICATION MANAGEMENT ====================

// Get all notification messages with pagination and filtering
export const    getNotificationMessagesApi = async (params = {}) => {
  const endPoint = `/admin/notifications-messages?${new URLSearchParams(params).toString()}`
  return await axios.get(endPoint, setJwtToken())
}

// Get notification message by ID
export const getNotificationMessageByIdApi = async (id) => {
  const endPoint = `/admin/notifications-messages/${id}`
  return await axios.get(endPoint, setJwtToken())
}

// Create new notification message
export const createNotificationMessageApi = async (messageData) => {
  const endPoint = `/admin/notifications-messages`
  return await axios.post(endPoint, messageData, setJwtToken())
}

// Update notification message
export const updateNotificationMessageApi = async (id, messageData) => {
  const endPoint = `/admin/notifications-messages/${id}`
  return await axios.put(endPoint, messageData, setJwtToken())
}

// Delete notification message
export const deleteNotificationMessageApi = async (id) => {
  const endPoint = `/admin/notifications-messages/${id}`
  return await axios.delete(endPoint, setJwtToken())
}

// Bulk operations on messages
export const bulkOperationApi = async (operation, messageIds, data = {}) => {
  const endPoint = `/admin/notifications-messages/bulk`
  return await axios.post(endPoint, { operation, messageIds, data }, setJwtToken())
}

// Get message statistics
export const getMessageStatsApi = async () => {
  const endPoint = `/admin/notifications-messages/stats/overview`
  return await axios.get(endPoint, setJwtToken())
}

// Reset message usage counts
export const resetMessageUsageApi = async (category = null) => {
  const endPoint = `/admin/notifications-messages/reset-usage`
  return await axios.get(endPoint, setJwtToken())
}

// Get available categories
export const getCategoriesApi = async () => {
  const endPoint = `/admin/notifications-messages/categories`
  return await axios.get(endPoint, setJwtToken())
}

// Get available languages
export const getLanguagesApi = async () => {
  const endPoint = `/admin/notifications-messages/languages`
  return await axios.get(endPoint, setJwtToken())
}

// Get target audiences
export const getTargetAudiencesApi = async () => {
  const endPoint = `/admin/notifications-messages/target-audiences`
  return await axios.get(endPoint, setJwtToken())
}

// Test notification message
export const testNotificationApi = async (id) => {
  const endPoint = `/admin/notifications-messages/${id}/test`
  return await axios.post(endPoint, {}, setJwtToken())
}

// Import messages
export const importMessagesApi = async (messages) => {
  const endPoint = `/admin/notifications-messages/import`
  return await axios.post(endPoint, { messages }, setJwtToken())
}

// Export messages
export const exportMessagesApi = async (params = {}) => {
      const endPoint = `/admin/notifications-messages/export?${new URLSearchParams(params).toString()}`
  return await axios.get(endPoint, setJwtToken())
}

// ==================== DASHBOARD & ANALYTICS ====================

// Get dashboard overview
export const getDashboardOverviewApi = async () => {
  const endPoint = `/admin/dashboard/overview`
  return await axios.get(endPoint, setJwtToken())
}

// Get category distribution
export const getCategoryDistributionApi = async () => {
  const endPoint = `/admin/dashboard/categories/distribution`
  return await axios.get(endPoint, setJwtToken())
}

// Get language distribution
export const getLanguageDistributionApi = async () => {
  const endPoint = `/admin/dashboard/languages/distribution`
  return await axios.get(endPoint, setJwtToken())
}

// Get timing distribution
export const getTimingDistributionApi = async () => {
  const endPoint = `/admin/dashboard/timing/distribution`
  return await axios.get(endPoint, setJwtToken())
}

// Get usage statistics
export const getUsageStatisticsApi = async () => {
  const endPoint = `/admin/dashboard/usage/statistics`
  return await axios.get(endPoint, setJwtToken())
}

// Get performance metrics
export const getPerformanceMetricsApi = async (days = 30) => {
  const endPoint = `/admin/dashboard/performance/metrics?days=${days}`
  return await axios.get(endPoint, setJwtToken())
}

// Get message health analysis
export const getMessageHealthApi = async () => {
  const endPoint = `/admin/dashboard/messages/health`
  return await axios.get(endPoint, setJwtToken())
}

// Get user engagement metrics
export const getUserEngagementApi = async () => {
  const endPoint = `/admin/dashboard/users/engagement`
  return await axios.get(endPoint, setJwtToken())
}

// Get message recommendations
export const getMessageRecommendationsApi = async () => {
  const endPoint = `/admin/dashboard/messages/recommendations`
  return await axios.get(endPoint, setJwtToken())
}

// Get system health status
export const getSystemHealthApi = async () => {
  const endPoint = `/admin/dashboard/system/health`
  return await axios.get(endPoint, setJwtToken())
}

// Generate monthly report
export const generateMonthlyReportApi = async (month = null, year = null) => {
  const params = {}
  if (month) params.month = month
  if (year) params.year = year
  const endPoint = `/admin/dashboard/reports/monthly?${new URLSearchParams(params).toString()}`
  return await axios.get(endPoint, setJwtToken())
}

// Get real-time dashboard data
export const getRealTimeDashboardApi = async () => {
  const endPoint = `/admin/dashboard/realtime`
  return await axios.get(endPoint, setJwtToken())
}

// Get dashboard widgets data
export const getDashboardWidgetsApi = async () => {
  const endPoint = `/admin/dashboard/widgets`
  return await axios.get(endPoint, setJwtToken())
}

// Get analytics summary
export const getAnalyticsSummaryApi = async (period = 30) => {
  const endPoint = `/admin/dashboard/analytics/summary?period=${period}`
  return await axios.get(endPoint, setJwtToken())
}

// Export dashboard data
export const exportDashboardDataApi = async (type = 'overview', format = 'json') => {
  const endPoint = `/admin/dashboard/export?type=${type}&format=${format}`
  return await axios.get(endPoint, setJwtToken())
}

// ==================== NOTIFICATION TRACKING & CONVERSION ====================

// Get notification batches
export const getNotificationBatchesApi = async (params = {}) => {
  const endPoint = `/admin/notifications-analytics/batches?${new URLSearchParams(params).toString()}`
  return await axios.get(endPoint, setJwtToken())
}

// Get batch tracking details
export const getBatchTrackingApi = async (batchId, params = {}) => {
  const endPoint = `/admin/notifications-analytics/batches/${batchId}/tracking?${new URLSearchParams(params).toString()}`
  return await axios.get(endPoint, setJwtToken())
}

// Get overall conversion stats
export const getConversionStatsApi = async (params = {}) => {
  const endPoint = `/admin/notifications-analytics/conversion-stats?${new URLSearchParams(params).toString()}`
  return await axios.get(endPoint, setJwtToken())
}

// ==================== UTILITY FUNCTIONS ====================

// Helper function to handle API responses
export const handleApiResponse = (response) => {
  if (response.data && response.data.success) {
    return response.data.data
  }
  throw new Error(response.data?.message || 'API request failed')
}

// Helper function to handle API errors
export const handleApiError = (error) => {
  const message = error.response?.data?.message || error.message || 'Something went wrong'
  throw new Error(message)
}

// Helper function to format message data for API
export const formatMessageData = (data) => {
  const formatted = { ...data }
  
  // Convert priority to number
  if (formatted.priority) {
    formatted.priority = parseInt(formatted.priority)
  }
  
  // Convert dayOfMonth, dayOfWeek, month to numbers if they exist
  if (formatted.dayOfMonth) {
    formatted.dayOfMonth = parseInt(formatted.dayOfMonth)
  }
  if (formatted.dayOfWeek) {
    formatted.dayOfWeek = parseInt(formatted.dayOfWeek)
  }
  if (formatted.month) {
    formatted.month = parseInt(formatted.month)
  }
  
  // Ensure tags is an array
  if (formatted.tags && typeof formatted.tags === 'string') {
    formatted.tags = formatted.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
  }
  
  return formatted
}

// Helper function to validate message data
export const validateMessageData = (data) => {
  const errors = []
  
  if (!data.title || data.title.trim() === '') {
    errors.push('Title is required')
  }
  
  if (!data.body || data.body.trim() === '') {
    errors.push('Body is required')
  }
  
  if (!data.category) {
    errors.push('Category is required')
  }
  
  if (data.timing && !/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(data.timing)) {
    errors.push('Invalid timing format. Use HH:MM format')
  }
  
  if (data.priority && (data.priority < 1 || data.priority > 10)) {
    errors.push('Priority must be between 1 and 10')
  }
  
  return errors
}

export default {
  // Enhanced notification management
  getNotificationMessagesApi,
  getNotificationMessageByIdApi,
  createNotificationMessageApi,
  updateNotificationMessageApi,
  deleteNotificationMessageApi,
  bulkOperationApi,
  getMessageStatsApi,
  resetMessageUsageApi,
  getCategoriesApi,
  getLanguagesApi,
  getTargetAudiencesApi,
  testNotificationApi,
  importMessagesApi,
  exportMessagesApi,
  
  // Dashboard & analytics
  getDashboardOverviewApi,
  getCategoryDistributionApi,
  getLanguageDistributionApi,
  getTimingDistributionApi,
  getUsageStatisticsApi,
  getPerformanceMetricsApi,
  getMessageHealthApi,
  getUserEngagementApi,
  getMessageRecommendationsApi,
  getSystemHealthApi,
  generateMonthlyReportApi,
  getRealTimeDashboardApi,
  getDashboardWidgetsApi,
  getAnalyticsSummaryApi,
  exportDashboardDataApi,
  
  // Utility functions
  handleApiResponse,
  handleApiError,
  formatMessageData,
  validateMessageData
}
